import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, canChangeStatus, WRITE_PERMISSION_DENIED } from '@/services/permissions';
import { getAgentSalespersonId } from '@/services/agent';
import { recordAudit } from '@/services/audit';

export interface ReturnItem {
  id: string;
  productName: string;
  sizeLabel: string;
  quantity: number;
  unitPrice: number;
  refundSubtotal: number;
  restockable: boolean;
}

export interface ReturnRecord {
  id: string;
  returnNumber: string;
  customerName: string;
  customerCity: string;
  saleNumber: string;
  salespersonName: string;
  reason: string;
  returnType: string;
  refundAmount: number;
  status: string;
  returnDate: string;
  items: ReturnItem[];
}

export interface ReturnsResult {
  data: ReturnRecord[];
}

// Consulta las devoluciones reales con fallback automático al mock.
export async function fetchReturns(): Promise<ReturnsResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const [returnsRes, itemsRes, salesRes, customersRes, variantsRes, productsRes, sizesRes, salespersonsRes] =
      await Promise.all([
        supabase.from('returns').select('*'),
        supabase.from('return_items').select('*'),
        supabase.from('sales').select('id, sale_number, customer_id, salesperson_id'),
        supabase.from('customers').select('id, first_name, last_name, city'),
        supabase.from('product_variants').select('*'),
        supabase.from('products').select('id, name'),
        supabase.from('sizes').select('*'),
        supabase.from('salespersons').select('id, full_name'),
      ]);

    if (returnsRes.error) throw new Error(returnsRes.error.message);
    if (itemsRes.error) throw new Error(itemsRes.error.message);
    if (salesRes.error) throw new Error(salesRes.error.message);
    if (customersRes.error) throw new Error(customersRes.error.message);
    if (variantsRes.error) throw new Error(variantsRes.error.message);
    if (productsRes.error) throw new Error(productsRes.error.message);
    if (sizesRes.error) throw new Error(sizesRes.error.message);

    const returns = (returnsRes.data ?? []) as Array<Record<string, any>>;

    const saleMap = new Map(
      ((salesRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s]),
    );
    const salespersonMap = new Map(
      ((salespersonsRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s.full_name]),
    );

    // Filtro por agente: solo ve las devoluciones de las ventas que él facturó.
    const agentSalespersonId = await getAgentSalespersonId();
    const visibleReturns = agentSalespersonId
      ? returns.filter((r) => saleMap.get(r.sale_id)?.salesperson_id === agentSalespersonId)
      : returns;
    const customerMap = new Map(
      ((customersRes.data ?? []) as Array<Record<string, any>>).map((c) => [c.id, c]),
    );
    const variantMap = new Map(
      ((variantsRes.data ?? []) as Array<Record<string, any>>).map((v) => [v.id, v]),
    );
    const productMap = new Map(
      ((productsRes.data ?? []) as Array<Record<string, any>>).map((p) => [p.id, p.name]),
    );
    const sizeMap = new Map(
      ((sizesRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s]),
    );

    const itemsByReturn = new Map<string, ReturnItem[]>();
    ((itemsRes.data ?? []) as Array<Record<string, any>>).forEach((ri) => {
      const variant = variantMap.get(ri.variant_id);
      const productName = variant ? productMap.get(variant.product_id) ?? '' : '';
      const size = variant ? sizeMap.get(variant.size_id) : undefined;
      const item: ReturnItem = {
        id: ri.id,
        productName,
        sizeLabel: size?.name ?? '',
        quantity: ri.quantity,
        unitPrice: Number(ri.refund_subtotal) / Math.max(1, Number(ri.quantity)),
        refundSubtotal: Number(ri.refund_subtotal),
        restockable: !!ri.restockable,
      };
      const list = itemsByReturn.get(ri.return_id) ?? [];
      list.push(item);
      itemsByReturn.set(ri.return_id, list);
    });

    const data = visibleReturns
      .map((r) => {
        const sale = saleMap.get(r.sale_id);
        const customer = sale ? customerMap.get(sale.customer_id) : undefined;
        return {
          id: r.id,
          returnNumber: r.return_number,
          customerName: customer
            ? `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim()
            : '',
          customerCity: customer?.city ?? '',
          saleNumber: sale?.sale_number ?? '',
          salespersonName: sale?.salesperson_id
            ? salespersonMap.get(sale.salesperson_id) ?? ''
            : '',
          reason: r.notes ?? '',
          returnType: r.return_type,
          refundAmount: Number(r.refund_amount),
          status: r.status,
          returnDate: r.created_at,
          items: itemsByReturn.get(r.id) ?? [],
        };
      })
      .sort((a, b) => (a.returnDate < b.returnDate ? 1 : -1));

    return { data };
  } catch (err) {
    console.error('[returns] Supabase connection failed:', err);
    throw err;
  }
}

// Entrada del formulario de devolución (crear).
export interface ReturnInput {
  saleId: string;
  variantId: string;
  quantity: number;
  refundAmount: number;
  reason: string;
  returnType: 'full_return' | 'partial_return';
}

export interface CreateReturnResult {
  success: boolean;
  returnId?: string;
  returnNumber?: string;
  error?: string;
}

// Registra una devolución de postventa en Supabase.
export async function createReturn(input: ReturnInput): Promise<CreateReturnResult> {
  if (!(await canWrite('returns'))) return { success: false, error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) {
    return { success: false, error: 'Supabase no está configurado.' };
  }

  const returnNumber = `R-${Date.now().toString().slice(-6)}`;

  try {
    const { data, error } = await supabase
      .from('returns')
      .insert({
        return_number: returnNumber,
        sale_id: input.saleId,
        return_type: input.returnType,
        refund_amount: input.refundAmount,
        status: 'pending',
        notes: input.reason.trim(),
      })
      .select('id');

    if (error) return { success: false, error: error.message };
    const ret = data?.[0] as { id: string } | undefined;
    if (!ret) return { success: false, error: 'No se pudo crear la devolución.' };

    recordAudit({
      entityType: 'returns',
      entityId: ret.id,
      action: 'CREATE',
      newValue: returnNumber,
    });

    const { error: itemError } = await supabase.from('return_items').insert({
      return_id: ret.id,
      variant_id: input.variantId,
      quantity: input.quantity,
      refund_subtotal: input.refundAmount,
      restockable: true,
    });
    if (itemError) console.warn('[returns] return_items insert failed:', itemError.message);

    return { success: true, returnId: ret.id, returnNumber };
  } catch (err) {
    console.warn('[returns] createReturn failed:', err);
    return { success: false, error: 'Error de conexión al crear la devolución.' };
  }
}

// Cambia el estado de una devolución (pending/received/refunded/rejected/closed)
// dejando registro en auditoría.
export async function updateReturnStatus(
  id: string,
  status: string,
): Promise<{ error: string | null }> {
  if (!(await canChangeStatus('returns'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { data: current } = await supabase
    .from('returns')
    .select('status')
    .eq('id', id)
    .maybeSingle();
  const prevStatus = (current as Record<string, any> | null)?.status ?? '';

  const { error } = await supabase.from('returns').update({ status }).eq('id', id);
  if (error) return { error: error.message };

  // Al pasar a 'refunded' se ejecuta el efecto contable: reingreso de stock
  // (o pérdida si no es restockeable) y reembolso como pago negativo. Una vez.
  if (status === 'refunded' && prevStatus !== 'refunded') {
    await applyReturnEffect(id);
  }

  recordAudit({
    entityType: 'returns',
    entityId: id,
    action: 'UPDATE',
    fieldName: 'status',
    oldValue: prevStatus,
    newValue: status,
  });
  return { error: null };
}

// Efecto contable de una devolución reembolsada: reingresa el stock restockeable
// (movimiento RETURN), registra pérdida (LOSS, delta 0) para lo no restockeable,
// y materializa el reembolso como un pago negativo vinculado por return_id.
async function applyReturnEffect(returnId: string): Promise<void> {
  const { data: ret } = await supabase
    .from('returns')
    .select('return_number, refund_amount')
    .eq('id', returnId)
    .maybeSingle();
  if (!ret) return;

  // Idempotencia: si ya existe un reembolso para esta devolución, no repetir.
  const { data: existingRefund } = await supabase
    .from('payments')
    .select('id')
    .eq('return_id', returnId)
    .maybeSingle();
  if (existingRefund) return;

  const { data: items } = await supabase
    .from('return_items')
    .select('variant_id, quantity, restockable')
    .eq('return_id', returnId);

  const returnNumber = (ret as Record<string, any>).return_number ?? '';

  for (const it of (items ?? []) as Array<Record<string, any>>) {
    const { data: inv } = await supabase
      .from('inventory')
      .select('available, reserved, consigned')
      .eq('variant_id', it.variant_id)
      .maybeSingle();
    const prev = Number(inv?.available ?? 0);
    const reserved = Number(inv?.reserved ?? 0);
    const consigned = Number(inv?.consigned ?? 0);
    const qty = Number(it.quantity);

    if (it.restockable) {
      const next = prev + qty;
      await supabase
        .from('inventory')
        .update({ available: next })
        .eq('variant_id', it.variant_id);

      await supabase.from('inventory_movements').insert({
        variant_id: it.variant_id,
        movement_type: 'RETURN',
        quantity: qty,
        prev_available: prev,
        new_available: next,
        prev_reserved: reserved,
        new_reserved: reserved,
        prev_consigned: consigned,
        new_consigned: consigned,
        unit_cost: 0,
        reason: `Devolución - ${returnNumber}`,
      });
    } else {
      // No reingresa stock: write-off LOSS con delta 0 (solo trazabilidad).
      await supabase.from('inventory_movements').insert({
        variant_id: it.variant_id,
        movement_type: 'LOSS',
        quantity: 0,
        prev_available: prev,
        new_available: prev,
        prev_reserved: reserved,
        new_reserved: reserved,
        prev_consigned: consigned,
        new_consigned: consigned,
        unit_cost: 0,
        reason: `Devolución no restockeable - ${returnNumber}`,
      });
    }
  }

  // Reembolso: pago negativo vinculado a la devolución (fuente de ejecución).
  const refundAmount = Number((ret as Record<string, any>).refund_amount ?? 0);
  if (refundAmount > 0) {
    await supabase.from('payments').insert({
      payment_number: `REF-${Date.now().toString().slice(-8)}`,
      return_id: returnId,
      method: 'other',
      status: 'refunded',
      amount: -refundAmount,
      transaction_ref: null,
    });
  }
}